/**
 * sessionStorage key for the Config Cheat Sheet Year/Event filters — shared by the
 * source (CheatSheet) and fleet (FleetCheatSheet) variants, like 'performanceFilters'
 * is shared by the performance history pages.
 */
export const CHEAT_SHEET_FILTERS_SESSION_KEY = 'cheatSheetFilters';

/**
 * Split comma-separated history metadata filter input (Year, Event, Config).
 * Each trimmed token is sent to the API as a separate contains-match value.
 */
export function splitMetadataFilterTokens(raw: string): string[] {
  return String(raw || '')
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

/** Canonical foiling-state values allowed by the cheat-sheet state filter. */
export const CHEAT_SHEET_STATE_OPTIONS = ['H0', 'H1', 'H2'] as const;

/**
 * Normalize a comma-separated state string to the canonical H0/H1/H2 set (uppercased,
 * deduped, invalid values dropped). Empty input falls back to ['H0'] — the cheat sheet is
 * always state-gated with H0-only as the default. Mirrors the server's normalizeStateTokens.
 */
export function normalizeCheatSheetStateTokens(raw: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const token of splitMetadataFilterTokens(raw).map((s) => s.toUpperCase())) {
    if ((CHEAT_SHEET_STATE_OPTIONS as readonly string[]).includes(token) && !seen.has(token)) {
      seen.add(token);
      out.push(token);
    }
  }
  return out.length > 0 ? out : ['H0'];
}

/**
 * Load the cheat-sheet Year/Event filters from sessionStorage (session persistence across
 * page navigation, not saved to persistent user settings). Returns null when nothing saved.
 */
export function loadCheatSheetFiltersFromSession(): { filterYear: string; filterEvent: string; filterState: string } | null {
  try {
    const saved = sessionStorage.getItem(CHEAT_SHEET_FILTERS_SESSION_KEY);
    if (!saved) return null;
    const filterData = JSON.parse(saved);
    const savedState = typeof filterData.filterState === 'string' ? filterData.filterState : '';
    return {
      filterYear: typeof filterData.filterYear === 'string' ? filterData.filterYear : '',
      filterEvent: typeof filterData.filterEvent === 'string' ? filterData.filterEvent : '',
      // Default/repair to H0 when missing or invalid (sessions predating the state filter).
      filterState: normalizeCheatSheetStateTokens(savedState).join(','),
    };
  } catch {
    return null;
  }
}

/** Save the cheat-sheet Year/Event/State filters to sessionStorage. */
export function saveCheatSheetFiltersToSession(filterYear: string, filterEvent: string, filterState: string): void {
  try {
    sessionStorage.setItem(
      CHEAT_SHEET_FILTERS_SESSION_KEY,
      JSON.stringify({ filterYear, filterEvent, filterState })
    );
  } catch {
    // Ignore errors
  }
}

/**
 * Append the active Year/Event/State contains-filters to a cheat-sheet fetch URL as JSON array
 * params (`&year=[...]&event=[...]&state=[...]`), matching the backend's
 * parseCheatSheetMetadataFilters. State is always sent (defaulting to ["H0"]), so the cheat
 * sheet is always state-gated.
 */
export function appendCheatSheetMetadataFiltersToUrl(
  url: string,
  filterYear: string,
  filterEvent: string,
  filterState: string
): string {
  let out = url;
  const years = splitMetadataFilterTokens(filterYear);
  if (years.length > 0) out += `&year=${encodeURIComponent(JSON.stringify(years))}`;
  const events = splitMetadataFilterTokens(filterEvent);
  if (events.length > 0) out += `&event=${encodeURIComponent(JSON.stringify(events))}`;
  const states = normalizeCheatSheetStateTokens(filterState);
  out += `&state=${encodeURIComponent(JSON.stringify(states))}`;
  return out;
}
